import { businessStore } from 'entities/index';
import React, { useEffect, useState } from 'react';
import { IServicesCard } from 'shared/api/booking/interfaces/services-list.interface';
import { IServiceCategory } from 'shared/api/booking/interfaces/service-categories.interface';
import { UniversalImage, RoundTriangle } from 'shared/ui';
import styles from './step-service.module.scss';
import { CheckMarkSvg } from 'shared/svg';
import { BookingApi } from 'shared/api';
import { useParams } from 'react-router-dom';
import {
  groupServicesByCategory,
  getUncategorized,
  IServiceGroup,
} from '../../model/group-services';

interface IStepService {
  selectedServiceId?: string;
  employeeId?: string;
  onSelect: (serviceId: string) => void;
}

export const StepService: React.FC<IStepService> = ({
  selectedServiceId,
  employeeId,
  onSelect,
}) => {
  const [services, setServices] = useState<IServicesCard[]>([]);
  const [categories, setCategories] = useState<IServiceCategory[]>([]);
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(
    {}
  );
  const [isUncategorizedOpen, setIsUncategorizedOpen] = useState(true);
  const params = useParams();
  const pointId = businessStore.pointId || params.pointId;

  useEffect(() => {
    loadServices();
    loadCategories();
  }, [employeeId]);

  const loadServices = async () => {
    if (!pointId) return;
    if (employeeId) {
      const result = await BookingApi.getServiceList({
        employeeId: employeeId,
        pointId,
      });
      setServices(result || []);
      return;
    }
    let results = businessStore.servicesList;
    if (results?.length) {
      setServices(results);
    } else {
      businessStore.initServicesList(pointId);
      results = businessStore.servicesList;
      setServices(results || []);
    }
  };

  const loadCategories = async () => {
    if (!pointId) return;
    let cats = businessStore.serviceCategories;
    if (!cats?.length) {
      cats = await BookingApi.getServiceCategories({ pointId });
      businessStore.serviceCategories = cats;
    }
    setCategories(cats || []);
    const initialOpen: Record<string, boolean> = {};
    (cats || []).forEach((c) => {
      initialOpen[c.id] = true;
    });
    setOpenCategories(initialOpen);
  };

  const toggleCategory = (id: string) => {
    setOpenCategories((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const serviceCard = (service: IServicesCard) => {
    const isSelected = selectedServiceId === service.id;
    return (
      <div
        className={`${styles.container__card} ${isSelected ? styles.container__card__selected : ''}`}
        key={service.id}
        onClick={() => onSelect(service.id)}
      >
        <div className={styles.card__data}>
          <div className={styles.card__img}>
            <UniversalImage imgSrc={service.imgUrl} />
          </div>
          <div className={styles.card__line}></div>
          <div className={styles.card__info}>
            <div className={styles.info__name}>{service.name}</div>
            <div className={styles.info__data}>
              <div className={styles.info__price}>{service.price} ₽</div>
              <div className={styles.info__duration}>
                {service.duration} мин.
              </div>
            </div>
          </div>
        </div>
        <div
          className={`${styles.card__checkbox} ${isSelected ? styles.card__checkbox__selected : ''}`}
        >
          {isSelected && <CheckMarkSvg />}
        </div>
      </div>
    );
  };

  const grouped = groupServicesByCategory(services, categories);
  const uncategorized = getUncategorized(services);

  return (
    <div className={styles.selector__container}>
      {grouped.map(
        ({ category: cat, services: catServices }: IServiceGroup) => (
          <div className={styles.category__container} key={cat.id}>
            <div
              className={styles.category__title}
              onClick={() => toggleCategory(cat.id)}
            >
              <div className={styles.category__name}>{cat.category}</div>
              <RoundTriangle isRotated={openCategories[cat.id]} />
            </div>
            {openCategories[cat.id] && (
              <div className={styles.category__services}>
                {catServices.map((service) => serviceCard(service))}
              </div>
            )}
          </div>
        )
      )}
      {uncategorized.length > 0 && (
        <div className={styles.category__container}>
          <div
            className={styles.category__title}
            onClick={() => setIsUncategorizedOpen(!isUncategorizedOpen)}
          >
            <div className={styles.category__name}>Без категории</div>
            <RoundTriangle isRotated={isUncategorizedOpen} />
          </div>
          {isUncategorizedOpen && (
            <div className={styles.category__services}>
              {uncategorized.map((service) => serviceCard(service))}
            </div>
          )}
        </div>
      )}
      {!grouped.length &&
        !uncategorized.length &&
        services.map((service) => serviceCard(service))}
    </div>
  );
};
